/** Auth pages that follow the saved light/dark preference (design S7). */
export const THEMED_AUTH_PATHS = [
  '/sign-in',
  '/create-account',
  '/forgot-password',
  '/sign-up',
  '/create-workspace'
] as const;

export const useShellVariant = () => {
  const route = useRoute();

  // i18n prefixes every non-default public route with /es. Shell selection must
  // operate on the locale-neutral route so legal content keeps its light shell
  // and footer in both supported locales.
  const shellPath = computed(() => route.path.replace(/^\/es(?=\/|$)/, '') || '/');

  const isAuthShell = computed(() => {
    return [
      '/sign-in',
      '/sign-up',
      '/forgot-password',
      '/create-account',
      '/create-workspace'
    ].includes(shellPath.value);
  });

  // Design S7: the auth pages rebuilt on the tokens follow the saved theme like
  // the other standalone pages (Ben Q11 B / Q28 B). /sign-up and
  // /create-workspace joined in cas-e079 once their dark literals were gone.
  const followsThemeAuthShell = computed(() =>
    (THEMED_AUTH_PATHS as readonly string[]).includes(shellPath.value)
  );

  const isEditorialShell = computed(() => {
    // cas-0573: /public/auth/* is the email-link sign-in, a standalone page that
    // follows the person's theme. It is not forced-light editorial content.
    if (shellPath.value.startsWith('/public/auth/')) return false;
    return (
      shellPath.value.startsWith('/public/') ||
      shellPath.value.startsWith('/docs') ||
      shellPath.value.startsWith('/privacy') ||
      shellPath.value.startsWith('/terms') ||
      shellPath.value.startsWith('/support') ||
      shellPath.value.startsWith('/content/')
    );
  });

  // Legal routes use the editorial layout but the application's dark visual
  // system. Keep this narrow so /content and /docs retain their current look.
  const isLegalShell = computed(() => {
    return ['/terms', '/privacy', '/support', '/public/terms', '/public/privacy'].some(
      path => shellPath.value === path || shellPath.value.startsWith(`${path}/`)
    );
  });

  const isAdminShell = computed(() => shellPath.value.startsWith('/admin'));

  const isCreatorShell = computed(() => {
    return !isAuthShell.value && !isEditorialShell.value && !isAdminShell.value;
  });

  const shellVariant = computed<'auth' | 'editorial' | 'admin' | 'creator'>(() => {
    if (isAuthShell.value) return 'auth';
    if (isEditorialShell.value) return 'editorial';
    if (isAdminShell.value) return 'admin';
    return 'creator';
  });

  // Auth matches the product dark canvas. Only non-legal editorial stays light.
  const shellThemeClass = computed(() =>
    !isLegalShell.value && isEditorialShell.value ? 'gabber-shell-light' : 'gabber-shell-dark'
  );

  return {
    isAuthShell,
    followsThemeAuthShell,
    isEditorialShell,
    isLegalShell,
    isAdminShell,
    isCreatorShell,
    shellVariant,
    shellThemeClass
  };
};
